import { HStack, Text } from '@/components/Atoms'
import {
  VARIANCE_QUICK_FILTERS,
  VarianceQuickFilter,
} from '@/hooks/queries/crt/useVarianceAnalysis'
import { PRODUCT_CATEGORY, ProductCategory } from '@repo/constants'
import { Button } from '@repo/ui/components'
import { useTranslation } from 'react-i18next'

/**
 * The scope params a preset may write. Selecting a preset clears every one of them before applying its
 * own, so a key the next preset does not carry cannot survive from the last one — which is how switching
 * Cigarettes to FMC used to leave the FMC view still scoped to cigarettes.
 *
 * It lives beside the slices so the two cannot drift: a param added below without being listed here
 * becomes the next stale one.
 */
export const QUICK_FILTER_SCOPE_KEYS = [
  'materialGroupCode',
  'packContentSize',
  'productTypeLike',
  'brandHouseCode',
] as const

export const VARIANCE_QUICK_FILTER_SLICES: Record<
  VarianceQuickFilter,
  { productCategory: ProductCategory } & Partial<
    Record<(typeof QUICK_FILTER_SCOPE_KEYS)[number], string[]>
  >
> = {
  fmc: { productCategory: PRODUCT_CATEGORY.FMC },
  cigarettes: {
    productCategory: PRODUCT_CATEGORY.FMC,
    materialGroupCode: ['ZFG100000'],
    packContentSize: ['20', '25'],
    brandHouseCode: ['MARL', 'PALL', 'DUMA', 'JPST'],
  },
  vapour: { productCategory: PRODUCT_CATEGORY.VAPOUR },
  'vuse-disposables': {
    productCategory: PRODUCT_CATEGORY.VAPOUR,
    // A family of codes rather than an explicit list
    productTypeLike: ['Disposable'],
  },
  nrt: { productCategory: PRODUCT_CATEGORY.NRT },
}

type QuickFilterBarProps = {
  selected: VarianceQuickFilter
  onSelect: (option: VarianceQuickFilter) => void
}

/**
 * A row of one-click presets over a single dimension. The options and their labels are supplied rather
 * than read from a constant, so the row does not need to know which dimension it is presenting.
 */
export function QuickFilterBar({ selected, onSelect }: QuickFilterBarProps) {
  const { t } = useTranslation('crt')

  return (
    <HStack gap="sm" className="flex-wrap items-center">
      <Text tone="muted" size="xs">
        {t('varianceAnalysis.quickFilters.label')}
      </Text>
      {VARIANCE_QUICK_FILTERS.map((option) => (
        <Button
          key={option}
          type="button"
          size="sm"
          variant={option === selected ? 'default' : 'secondary'}
          onClick={() => onSelect(option)}
        >
          {t(`varianceAnalysis.quickFilters.${option}`)}
        </Button>
      ))}
    </HStack>
  )
}
